/**
 * Thin wrapper around the RevenueCat Capacitor SDK.
 *
 * - Auto-configures on app start when running on a native platform.
 * - Exposes a small typed API used by the Paywall component.
 * - All methods are safe to call from the web — they no-op or throw `WEB_NOT_SUPPORTED`.
 *
 * RevenueCat handles:
 *   - Receipt validation with Apple's servers
 *   - Cross-device entitlement state
 *   - Restore purchases
 *   - Promotional / trial offers
 *
 * Our backend listens to RevenueCat's webhook to flip `user.plan = pro`
 * server-side — see /api/iap/revenuecat/webhook.
 */

import { isNative, isIOS } from "./platform";

let configured = false;
let configuringPromise = null; // prevent overlapping configure() calls
let inFlightFetch = null; // prevent overlapping getOfferings() calls
let PurchasesMod = null;
let purchasesLoadPromise = null; // eager-loaded once at module-load (T13)
let purchasesLoadError = null; // last error from the eager load attempt

// RevenueCat public API key. Frontend-safe (it's a public key — receipt
// validation still happens server-side via RevenueCat → our webhook).
// Configured at build time via REACT_APP_REVENUECAT_IOS_KEY.
const RC_IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";

/**
 * T13 / Build 55 — Eagerly load the RevenueCat Capacitor module at
 * module-import time (i.e. as soon as `iap.js` is first required by the
 * React tree, which happens during boot via `auth.js`). The import
 * resolves long before the user opens the paywall, so the tap path
 * never has to await a dynamic import.
 *
 * The dynamic import is still kept here (rather than a static
 * `import { Purchases } …`) so the **web** bundle doesn't pull native
 * Swift bridging stubs. But on iOS we kick it off immediately and
 * cache both the module and any load error.
 */
function startLoadPurchases() {
  if (purchasesLoadPromise) return purchasesLoadPromise;
  purchasesLoadPromise = (async () => {
    try {
      console.info("[IAP] T13 eager loadPurchases start");
      const mod = await import("@revenuecat/purchases-capacitor");
      PurchasesMod = mod.Purchases;
      console.info("[IAP] T13 eager loadPurchases OK");
      return PurchasesMod;
    } catch (e) {
      purchasesLoadError = e;
      console.error("[IAP] T13 eager loadPurchases FAILED:", e?.message || e);
      throw e;
    }
  })();
  return purchasesLoadPromise;
}

// Kick the eager load IMMEDIATELY on module evaluation when running
// natively on iOS. On web this is a no-op. Errors are swallowed here;
// they're re-surfaced inline at the call sites via `purchasesLoadError`.
if (isNative() && isIOS()) {
  startLoadPurchases().catch(() => {
    // Already logged inside startLoadPurchases — fire-and-forget at boot.
  });
}

async function loadPurchases() {
  if (PurchasesMod) return PurchasesMod;
  // Eager load may still be in-flight; await it. If it failed previously,
  // re-throw the captured error immediately instead of retrying forever.
  if (purchasesLoadError) throw purchasesLoadError;
  return startLoadPurchases();
}

/**
 * Wrap any promise with a hard timeout. Rejects with `TIMEOUT_<label>_<ms>ms`
 * if the promise hasn't settled by then. Used to guarantee the paywall
 * never hangs forever on a StoreKit / RevenueCat call that never returns —
 * Apple's StoreKit can silently stall after fresh Paid Apps Agreement
 * activation, sandbox-tester auth issues, or device network hiccups.
 */
function withTimeout(promise, ms, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`TIMEOUT_${label}_${ms}ms`));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Configure RevenueCat once per app session. Called from App.js on mount.
 * The `appUserId` lets RevenueCat tie iOS purchases to the same logged-in
 * user we have on the backend, so a sub started on iOS unlocks Pro on web.
 */
export async function configureIAP(appUserId) {
  if (!isNative() || !isIOS()) return;
  if (configured) return;
  if (configuringPromise) {
    // Prevent overlapping configure() calls — return the same in-flight promise.
    console.info("[IAP] configure(): already in-flight, awaiting existing promise");
    return configuringPromise;
  }
  if (!RC_IOS_KEY) {
    console.error("[IAP] REACT_APP_REVENUECAT_IOS_KEY missing — IAP DISABLED");
    return;
  }
  // Always-on diagnostic so we can verify which RC project the build is
  // pointing at, even in TestFlight prod. We only log the prefix + length,
  // never the full key. Apple iOS public keys begin with `appl_`.
  const keyPrefix = RC_IOS_KEY.slice(0, 9);
  console.info(
    `[IAP] configure(): key=${keyPrefix}…(len=${RC_IOS_KEY.length}) appUserId=${appUserId || "anon"}`,
  );
  configuringPromise = (async () => {
    try {
      console.info("[IAP] step:configure_start");
      const Purchases = await loadPurchases();
      await withTimeout(
        Purchases.configure({
          apiKey: RC_IOS_KEY,
          appUserID: appUserId || null,
        }),
        15000,
        "CONFIGURE",
      );
      configured = true;
      console.info("[IAP] step:configure_end OK");
    } catch (e) {
      console.error("[IAP] configure() FAILED:", e?.message || e, e);
      throw e;
    } finally {
      configuringPromise = null;
    }
  })();
  return configuringPromise;
}

/**
 * Once the user signs in with Google, tell RevenueCat so future purchases
 * attach to their user_id. Safe to call multiple times.
 */
export async function identifyIAP(userId) {
  if (!isIOS() || !configured || !userId) return;
  const Purchases = await loadPurchases();
  await Purchases.logIn({ appUserID: userId });
}

/**
 * On logout, reset RevenueCat so the next anonymous purchase doesn't
 * get attached to the previous user.
 */
export async function resetIAP() {
  if (!isIOS() || !configured) return;
  const Purchases = await loadPurchases();
  try {
    await Purchases.logOut();
  } catch (e) {
    // logOut throws if already anonymous — safe to ignore.
    if (process.env.NODE_ENV !== "production") {
      console.warn("[IAP] logOut warning:", e?.message || e);
    }
  }
}

/**
 * T49.T (Build 81) — Apple 3.1.2(c) rejection. The ASC subscription
 * products do not currently have a free-trial introductory offer
 * configured. Even when StoreKit historically returned an introPrice
 * (e.g. legacy local StoreKit config files), the paywall must never
 * surface "X days free" copy until ASC actually approves a free
 * trial offer.
 *
 * This function now always returns null so every downstream caller
 * renders the simple price line + plain "Subscribe Yearly/Monthly"
 * CTA. Re-enable once ASC approves a real intro offer by restoring
 * the original extraction logic from git history.
 */
function extractIntroTrialLabel(storeProduct) {
  void storeProduct;
  return null;
}

/**
 * Fetch the offerings configured in the RevenueCat dashboard.
 * Returns a flat list of packages with display info ready for the paywall.
 * Shape: [{ identifier, title, price_string, period, intro_trial_label }]
 */
/**
 * Fetch the offerings configured in the RevenueCat dashboard.
 * Returns a flat list of packages with display info ready for the paywall.
 * Shape: [{ identifier, title, price_string, period, intro_trial_label }]
 *
 * Defensive behaviours:
 *   1. Calls configureIAP() first (idempotent) in case the auth-driven
 *      configure failed or hasn't run yet when the paywall mounts.
 *   2. Retries with exponential backoff when RC returns no current offering
 *      or zero packages — the iOS StoreKit cache can be stale immediately
 *      after Paid Apps Agreement activation, and a 1–4s delay typically
 *      lets it warm up.
 */
export async function fetchOfferings() {
  if (!isIOS()) throw new Error("WEB_NOT_SUPPORTED");
  // Dedupe: if a fetch is already in-flight, return the same promise instead
  // of starting a second concurrent call (which would race the RC SDK).
  if (inFlightFetch) {
    console.info("[IAP] fetchOfferings: already in-flight, returning existing promise");
    return inFlightFetch;
  }
  inFlightFetch = (async () => {
    try {
      // Ensure configure is fully complete before fetching. configureIAP() is
      // idempotent — if already done, returns immediately. Otherwise it
      // dedupes via its own in-flight promise (configuringPromise).
      console.info("[IAP] step:ensure_configure_start");
      try {
        await configureIAP(null);
      } catch (e) {
        console.warn("[IAP] fetchOfferings: configureIAP() threw (continuing anyway):", e?.message || e);
      }
      console.info(`[IAP] step:ensure_configure_end configured=${configured}`);
      const Purchases = await loadPurchases();
      const ATTEMPT_TIMEOUT_MS = 8000;
      console.info(`[IAP] step:getOfferings_start (timeout ${ATTEMPT_TIMEOUT_MS}ms)`);
      let result;
      try {
        result = await withTimeout(
          Purchases.getOfferings(),
          ATTEMPT_TIMEOUT_MS,
          "FETCH_OFFERINGS",
        );
      } catch (e) {
        console.error(
          "[IAP] step:getOfferings_end THREW:",
          e?.code || "no-code",
          e?.message || e,
        );
        throw e;
      }
      const { current, all } = result || {};
      const allKeys = all ? Object.keys(all) : [];
      console.info(
        `[IAP] step:getOfferings_end OK: current=${current?.identifier || "NULL"} all=[${allKeys.join(",")}]`,
      );
      if (!current || (current.availablePackages || []).length === 0) {
        console.warn(
          `[IAP] No packages. current=${current?.identifier || "NULL"} pkgs=${(current?.availablePackages || []).length}. Returning [].`,
        );
        return [];
      }
      const pkgs = current.availablePackages;
      console.info(`[IAP] current.availablePackages.length=${pkgs.length}`);
      pkgs.forEach((p, idx) => {
        const sp = p.storeProduct || {};
        const ip = sp.introPrice || sp.introductoryPrice || null;
        console.info(
          `[IAP] pkg[${idx}] id=${p.identifier} type=${p.packageType} productId=${sp.productIdentifier || "?"} price=${sp.priceString || "?"} intro=${ip ? JSON.stringify({ price: ip.price, period: ip.period || ip.periodUnit }) : "none"}`,
        );
      });
      return pkgs.map((p) => ({
        identifier: p.identifier,
        title: p.storeProduct?.title || p.identifier,
        price_string: p.storeProduct?.priceString || "",
        period:
          p.packageType === "ANNUAL"
            ? "year"
            : p.packageType === "MONTHLY"
            ? "month"
            : p.packageType || "",
        intro_trial_label: extractIntroTrialLabel(p.storeProduct),
        raw: p,
      }));
    } finally {
      inFlightFetch = null;
    }
  })();
  return inFlightFetch;
}


// === Product-direct fallback ==========================================
// Hard-coded product identifiers — must match App Store Connect exactly.
// Used when RevenueCat's `getOfferings()` returns empty or stalls (common
// in sandbox/TestFlight propagation windows). This bypasses RC's offering
// wiring entirely and queries StoreKit by product ID so the paywall CTAs
// become tappable for App Review.
const APPLE_PRODUCT_IDS = [
  "tiplingo_pro_yearly",
  "tiplingo_pro_monthly",
];

export async function fetchProductsDirect() {
  if (!isIOS()) return [];
  try {
    await configureIAP(null);
  } catch (e) {
    console.warn("[IAP] fetchProductsDirect: configureIAP threw:", e?.message || e);
  }
  const Purchases = await loadPurchases();
  console.info(`[IAP] step:getProducts_start (ids=${APPLE_PRODUCT_IDS.join(",")})`);
  // Try TWO call shapes — the Capacitor RC plugin changed between v12 and
  // v13. Newer versions accept `{ productIdentifiers, type }`; older
  // versions accept `{ productIdentifiers }` alone. We try with type first
  // (more specific, faster on v13) and fall back without.
  const attempts = [
    { productIdentifiers: APPLE_PRODUCT_IDS, type: "SUBSCRIPTION" },
    { productIdentifiers: APPLE_PRODUCT_IDS },
    // Last-resort: some plugin versions accept just an array
    APPLE_PRODUCT_IDS,
  ];
  let result = null;
  let lastError = null;
  for (let i = 0; i < attempts.length; i++) {
    try {
      result = await withTimeout(
        Purchases.getProducts(attempts[i]),
        15000,
        `GET_PRODUCTS_ATTEMPT_${i + 1}`,
      );
      const probe = result?.products || result || [];
      console.info(
        `[IAP] getProducts attempt ${i + 1} shape=${typeof result} keys=${result ? Object.keys(result).join(",") : "n/a"} count=${Array.isArray(probe) ? probe.length : "?"}`,
      );
      if (Array.isArray(probe) && probe.length > 0) break;
    } catch (e) {
      lastError = e;
      console.warn(`[IAP] getProducts attempt ${i + 1} threw:`, e?.message || e);
    }
  }
  const products = result?.products || (Array.isArray(result) ? result : []);
  console.info(`[IAP] step:getProducts_end OK count=${products.length}${lastError ? ` lastError=${lastError?.message}` : ""}`);
  return products.map((sp) => ({
    identifier: sp.identifier?.includes("yearly") ? "$rc_annual" : "$rc_monthly",
    title: sp.title || sp.identifier,
    price_string: sp.priceString || "",
    period: sp.identifier?.includes("yearly") ? "year" : "month",
    intro_trial_label: extractIntroTrialLabel(sp),
    raw: { storeProduct: sp, _direct: true },
  }));
}

/**
 * Trigger an Apple StoreKit purchase modal for the given package.
 * Returns { entitled: bool, expires: iso-string|null } on success.
 * Throws on cancellation or failure.
 *
 * T14 / Build 56 — REQUIRES `configureIAP()` to have completed successfully
 * before calling `getProducts()`. Prior attempts (T11→T13) tried to skip,
 * background-fire, or short-wait configure to dodge a hang — but RC's
 * native getProducts throws `"Purchases must be configured before calling
 * this function"` if it sees a partial configure, so there is no shortcut.
 *
 * Strategy:
 *   1. Step `"Configuring SDK"` — call `configureIAP(appUserId)` with a hard
 *      15s outer timeout. The inner `Purchases.configure()` also has 15s.
 *   2. On success → step `"SDK configured"`.
 *   3. On timeout → throw `"Subscription service timed out"` and surface
 *      inline.
 *   4. Only after success do we step `"Fetching Product"` and call
 *      getProducts (3 plugin-shape attempts × 10s).
 *   5. Then step `"Opening Native Sheet"` (purchaseStoreProduct, 60s).
 *
 * onStep(stepLabel, detail?) — callback for inline UI progress.
 */
export async function purchaseByProductId(productId, onStep, appUserId) {
  const step = (label, detail) => {
    const line = detail ? `${label} — ${detail}` : label;
    console.info(`[IAP] T14 step: ${line}`);
    try { onStep && onStep(label, detail); } catch (_e) { /* swallow callback errors */ }
  };

  if (!isIOS()) {
    step("Error: WEB_NOT_SUPPORTED");
    throw new Error("WEB_NOT_SUPPORTED");
  }

  // Resolve the Purchases module BEFORE configure — needed inside configureIAP
  // anyway, and this gives us a clear early error if the SDK never loaded.
  let Purchases;
  if (PurchasesMod) {
    Purchases = PurchasesMod;
  } else if (purchasesLoadError) {
    const msg = purchasesLoadError?.message || String(purchasesLoadError);
    step(`Error: Purchases SDK unavailable — ${msg}`);
    throw new Error(`Purchases SDK unavailable: ${msg}`);
  } else {
    step("Awaiting eager SDK import", "max 8s");
    try {
      Purchases = await withTimeout(
        startLoadPurchases(),
        8000,
        "AWAIT_EAGER_SDK_IMPORT",
      );
    } catch (e) {
      const msg = e?.message || String(e);
      step(`Error: Failed to load Purchases SDK — ${msg}`);
      throw new Error(`Failed to load Purchases SDK: ${msg}`);
    }
  }

  // Step 1 — REQUIRE configure. RC native getProducts will throw
  // "Purchases must be configured before calling this function" otherwise.
  if (configured) {
    step("SDK already configured");
  } else {
    // Diagnostic: confirm what we're configuring with. Logs key prefix +
    // length + the appUserId we'll pass. Never logs the full key.
    const keyOk = !!RC_IOS_KEY;
    const keyDetail = keyOk
      ? `key=${RC_IOS_KEY.slice(0, 9)}… len=${RC_IOS_KEY.length} appUserId=${appUserId || "anon"}`
      : "key=MISSING (REACT_APP_REVENUECAT_IOS_KEY not set in this build)";
    step("Configuring SDK", keyDetail);
    if (!keyOk) {
      const msg = "REACT_APP_REVENUECAT_IOS_KEY is missing from this build";
      step(`Error: ${msg}`);
      throw new Error(msg);
    }
    try {
      await withTimeout(configureIAP(appUserId || null), 15000, "CONFIGURE_REQUIRED");
      if (!configured) {
        // configureIAP returned without setting `configured` (e.g. swallowed
        // an internal error). Treat as a hard failure.
        const msg = "Subscription service did not finish initialising";
        step(`Error: ${msg}`);
        throw new Error(msg);
      }
      step("SDK configured");
    } catch (e) {
      const raw = e?.message || String(e);
      const msg = raw.startsWith("TIMEOUT_CONFIGURE_REQUIRED")
        ? "Subscription service timed out (15s)"
        : `Subscription service failed: ${raw}`;
      step(`Error: ${msg}`);
      throw new Error(msg);
    }
  }

  // Step 2 — Fetch StoreProduct with hard 10s timeout per attempt
  step("Fetching Product", productId);
  const attempts = [
    { productIdentifiers: [productId], type: "SUBSCRIPTION" },
    { productIdentifiers: [productId] },
    [productId],
  ];
  let storeProduct = null;
  let lastFetchErr = null;
  for (let i = 0; i < attempts.length && !storeProduct; i++) {
    try {
      const r = await withTimeout(
        Purchases.getProducts(attempts[i]),
        10000,
        `GETP_FOR_PURCHASE_${i + 1}`,
      );
      const list = r?.products || (Array.isArray(r) ? r : []);
      console.info(`[IAP] T14 getProducts attempt ${i + 1} count=${list.length} keys=${r ? Object.keys(r).join(",") : "n/a"}`);
      storeProduct = list.find((p) => p.identifier === productId) || list[0];
      if (storeProduct) {
        console.info(`[IAP] T14 resolved StoreProduct via attempt ${i + 1}`);
        break;
      }
    } catch (e) {
      lastFetchErr = e;
      console.warn(`[IAP] T14 getProducts attempt ${i + 1} threw:`, e?.message || e);
    }
  }
  if (!storeProduct) {
    const detail = lastFetchErr?.message ? ` (${lastFetchErr.message})` : "";
    const msg = `Product not available from StoreKit: ${productId}${detail}`;
    step(`Error: ${msg}`);
    throw new Error(msg);
  }

  // Step 3 — Open native purchase sheet with hard 60s timeout (gives the
  // user time to authenticate via FaceID / Touch ID / password).
  step("Opening Native Sheet", productId);
  let result;
  try {
    result = await withTimeout(
      Purchases.purchaseStoreProduct({ product: storeProduct }),
      60000,
      "PURCHASE_STORE_PRODUCT",
    );
  } catch (e) {
    const msg = e?.message || String(e);
    step(`Error: ${msg}`);
    throw e;
  }
  step("Done");
  return summarize(result.customerInfo);
}

export async function purchasePackage(pkg) {
  if (!isIOS()) throw new Error("WEB_NOT_SUPPORTED");
  const Purchases = await loadPurchases();
  const raw = pkg.raw || pkg;
  let result;
  // Direct-fetch product (from fetchProductsDirect) → use purchaseStoreProduct
  if (raw?._direct && raw?.storeProduct) {
    console.info("[IAP] purchase via purchaseStoreProduct (direct fetch fallback)");
    result = await Purchases.purchaseStoreProduct({ product: raw.storeProduct });
  } else {
    // Offerings-derived package → use purchasePackage
    console.info("[IAP] purchase via purchasePackage (offerings)");
    result = await Purchases.purchasePackage({ aPackage: raw });
  }
  return summarize(result.customerInfo);
}

/**
 * Required by Apple — restore any prior IAP for this Apple ID.
 */
export async function restorePurchases() {
  if (!isIOS()) throw new Error("WEB_NOT_SUPPORTED");
  const Purchases = await loadPurchases();
  const info = await Purchases.restorePurchases();
  return summarize(info.customerInfo);
}

/**
 * Pull current entitlement state from RevenueCat (no network call to Apple).
 * Useful right after login or app launch to seed UI.
 */
export async function getCustomerInfo() {
  if (!isIOS()) throw new Error("WEB_NOT_SUPPORTED");
  const Purchases = await loadPurchases();
  const info = await Purchases.getCustomerInfo();
  return summarize(info.customerInfo);
}

function summarize(customerInfo) {
  const ent = customerInfo?.entitlements?.active?.pro;
  return {
    entitled: !!ent,
    expires: ent?.expirationDate || null,
    product_id: ent?.productIdentifier || null,
    raw: customerInfo,
  };
}
